// Task interfaces for the Task module

export interface Task {
  id: number;
  organizationId: number;
  branchId?: number;
  name: string;
  categoryId?: number;
  startDate?: string;
  dueDate?: string;
  reminderDate?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;

  // Automation provenance tracking
  isAutoCreated?: boolean;
  autoCreatedByRuleId?: number;
  createdByUserId?: number;
  ruleName?: string;

  // Completion condition fields
  completionConditionType?: string | null;
  completionConditionConfig?: CompletionConditionConfig | null;
  completionConditionProgress?: Record<string, unknown>;
  completionConditionMet?: boolean;
  completionConditionMetAt?: string | null;
  completionConditionMetBy?: number | null;

  // Joined fields for display
  category_name?: string;
  category_color?: string;
  creator_first_name?: string;
  creator_last_name?: string;
  leads?: TaskLead[];
  assignees?: TaskUser[];
  supervisors?: TaskUser[];
}

export interface TaskCategory {
  id: number;
  organizationId: number;
  name: string;
  description?: string;
  color: string;
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaskLead {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone?: string;
}

export interface TaskUser {
  id: number;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  avatar_url?: string;
  avatarUrl?: string;
}

export interface TaskFormData {
  name: string;
  categoryId: number | null;
  startDate: string;
  dueDate: string;
  reminderDate: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  description: string;
  leadIds: number[];
  assigneeIds: number[];
  supervisorIds: number[];
  completionConditionType?: string | null;
  completionConditionConfig?: CompletionConditionConfig | null;
}

export type CompletionConditionType = 'document_upload' | 'call_completed' | 'form_submitted' | 'email_sent' | null;

export interface CompletionConditionConfig {
  documentTypeIds?: number[];
  requireAll?: boolean;
  callTypes?: string[];
  minDuration?: number | null;
  formId?: number | null;
}

export interface TaskStats {
  total_tasks: number;
  pending_tasks: number;
  in_progress_tasks: number;
  completed_tasks: number;
  cancelled_tasks: number;
  overdue_tasks: number;
  due_today: number;
  due_this_week: number;
}

// API Response types
export interface TasksResponse {
  success: boolean;
  data: Task[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface TaskResponse {
  success: boolean;
  data: Task;
  message?: string;
}

export interface TaskCategoriesResponse {
  success: boolean;
  data: TaskCategory[];
}

export interface TaskStatsResponse {
  success: boolean;
  data: TaskStats;
}

// Priority options
export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
] as const;

// Status options
export const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' }
] as const;

// Utility functions
export const getPriorityColor = (priority: string) => {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority);
  return option ? option.color : 'bg-gray-100 text-gray-800';
};

export const getStatusColor = (status: string) => {
  const option = STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.color : 'bg-gray-100 text-gray-800';
};

export const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const isOverdue = (dueDate: string) => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

export const isDueToday = (dueDate: string) => {
  if (!dueDate) return false;
  const today = new Date();
  const due = new Date(dueDate);
  return due.toDateString() === today.toDateString();
};

export const isDueThisWeek = (dueDate: string) => {
  if (!dueDate) return false;
  const today = new Date();
  const due = new Date(dueDate);
  const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  return due >= today && due <= weekFromNow;
};
